import { ChartType, Plugin } from 'chart.js'

import {
  ARROW_H,
  ARROW_W,
  getFont,
  getPillText,
  PADDING_X,
  PILL_H,
  RADIUS,
  ReferenceLine
} from './referenceLinePill'

export type DrawVerticalLinesOpts = {
  lines: ReferenceLine[]
  decimals?: number
  significantFigures?: number
}

/**
 * Chart.js plugin: vertical reference lines down the chart area, each with
 * a pill at the top pointing down at it. The horizontal plugin's twin.
 */
export const drawVerticalLines: Plugin<ChartType, DrawVerticalLinesOpts> = {
  id: 'drawVerticalLines',
  afterDraw(chart, _args, opts) {
    const lines = opts?.lines ?? []

    if (lines.length === 0) return

    const { ctx, chartArea, scales } = chart
    const xScale = scales.x

    if (!xScale) return

    lines.forEach((line) => {
      const xPixel = xScale.getPixelForValue(line.value)

      if (xPixel < chartArea.left || xPixel > chartArea.right) return

      // dashed line down the chart area
      ctx.save()
      ctx.strokeStyle = line.color
      ctx.lineWidth = 2
      ctx.setLineDash([5, 3])
      ctx.beginPath()
      ctx.moveTo(xPixel, chartArea.top)
      ctx.lineTo(xPixel, chartArea.bottom)
      ctx.stroke()
      ctx.restore()

      // pill at the top, kept inside the chart area horizontally
      ctx.save()
      ctx.font = getFont()

      const text = getPillText(line, opts.decimals, opts.significantFigures)
      const pillW = ctx.measureText(text).width + PADDING_X * 2
      const center = Math.max(
        chartArea.left + pillW / 2,
        Math.min(chartArea.right - pillW / 2, xPixel)
      )

      const top = chartArea.top
      const bottom = top + PILL_H
      const left = center - pillW / 2
      const right = center + pillW / 2
      const arrowX = Math.max(
        left + RADIUS + ARROW_H / 2,
        Math.min(right - RADIUS - ARROW_H / 2, xPixel)
      )

      ctx.beginPath()
      ctx.moveTo(left + RADIUS, top)
      ctx.lineTo(right - RADIUS, top)
      ctx.quadraticCurveTo(right, top, right, top + RADIUS)
      ctx.lineTo(right, bottom - RADIUS)
      ctx.quadraticCurveTo(right, bottom, right - RADIUS, bottom)
      ctx.lineTo(arrowX + ARROW_H / 2, bottom) // arrow right base
      ctx.lineTo(xPixel, bottom + ARROW_W) // arrow tip
      ctx.lineTo(arrowX - ARROW_H / 2, bottom) // arrow left base
      ctx.lineTo(left + RADIUS, bottom)
      ctx.quadraticCurveTo(left, bottom, left, bottom - RADIUS)
      ctx.lineTo(left, top + RADIUS)
      ctx.quadraticCurveTo(left, top, left + RADIUS, top)
      ctx.closePath()

      ctx.fillStyle = line.color
      ctx.fill()

      ctx.fillStyle = '#ffffff'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(text, center, top + PILL_H / 2)

      ctx.restore()
    })
  }
}
